/**
 * Gradebook Page
 * Modern, clean UI for displaying student assignments and quizzes
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiUser, FiUsers, FiFileText, FiBook, FiChevronDown, FiChevronUp, FiEdit3, FiX, FiCheckSquare, FiSquare, FiEye, FiCalendar, FiCheckCircle, FiXCircle, FiClock, FiAlertCircle, FiInfo, FiPlus, FiPackage } from 'react-icons/fi';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import SuccessModal from '../../components/modals/SuccessModal';
import SubmissionViewModal from '../../components/modals/SubmissionViewModal';
import BulkGrading from '../../components/grading/BulkGrading';
import RubricBuilder from '../../components/grading/RubricBuilder';
import RubricSelector from '../../components/grading/RubricSelector';
import ExternalItemModal from '../../components/grading/ExternalItemModal';
import ExternalItemsManager from '../../components/grading/ExternalItemsManager';

// ============================================================================
// LAYOUT COMPONENTS
// ============================================================================

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  padding: 1rem 1rem;
  background: #F7F5F2;
  
  @media (min-width: 640px) {
    padding: 1.5rem 1.5rem;
  }
  
  @media (min-width: 1024px) {
    padding: 2rem 2rem;
  }
`;

const DashboardContainer = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 24px;
  align-items: start;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const Container = styled.div`
  width: 100%;
`;

const Sidebar = styled.aside`
  position: sticky;
  top: 24px;
  height: fit-content;
  align-self: start;
  
  @media (max-width: 900px) {
    position: static;
    order: -1;
  }
`;

const SidebarPanel = styled.div`
  background: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 900px) {
    display: none;
  }
`;

const SidebarLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 12px;
  font-family: 'Inter', sans-serif;
`;

const SidebarNav = styled.nav`
  display: flex;
  flex-direction: column;
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NavListItem = styled.li`
  display: block;
`;

const NavItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: transparent;
  border: none;
  border-radius: 12px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  position: relative;
  min-height: 44px;
  
  &:hover {
    background-color: rgba(15, 23, 42, 0.04);
    color: var(--text-primary);
  }
  
  ${props => props.$active && `
    background-color: rgba(216, 184, 137, 0.1);
    color: var(--primary-color);
    font-weight: 600;
    border: 1px solid rgba(216, 184, 137, 0.2);
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 60%;
      background: var(--primary-color);
      border-radius: 0 2px 2px 0;
    }
  `}
  
  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
  }
`;

const NavLabel = styled.span`
  flex: 1;
  text-align: left;
`;

const NavBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => props.$active ? 'rgba(216, 184, 137, 0.2)' : 'rgba(15, 23, 42, 0.08)'};
  color: ${props => props.$active ? 'var(--primary-color)' : 'var(--text-muted)'};
`;

const MobileNavSelect = styled.select`
  display: none;
  width: 100%;
  padding: 12px 14px;
  padding-right: 36px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  background: #FFFFFF;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23334155' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  min-height: 44px;
  box-sizing: border-box;
  margin-bottom: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 900px) {
    display: block;
  }
  
  @media (max-width: 480px) {
    font-size: 15px;
    padding: 12px 14px;
    padding-right: 40px;
    min-height: 48px;
  }
`;

const MainContent = styled.main`
  width: 100%;
  background: #F7F5F2;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
  }
`;

const StatCard = styled.div`
  background: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const StatCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const StatCardTitle = styled.h3`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatCardIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${props => props.$color || 'var(--background-secondary)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$iconColor || 'var(--text-primary)'};
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const StatCardValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;
  margin-bottom: 0.25rem;
`;

const StatCardSubtext = styled.div`
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

const Header = styled.div`
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: clamp(1.75rem, 2.5vw + 1rem, 2.25rem);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 0.9375rem;
  color: var(--text-secondary);
  margin: 0;
  font-weight: 400;
  line-height: 1.5;
`;

const ToolbarCard = styled.div`
  background: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 20px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const ToolbarGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 0.875rem 1rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  background: #FFFFFF;
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 400;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.5;
  transition: all 0.2s ease;
  min-height: 44px;
  
  &:hover {
    border-color: rgba(0, 0, 0, 0.15);
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
  
  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const FilterDropdownWrapper = styled.div`
  position: relative;
  display: inline-flex;
  min-width: 160px;
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
  }
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  background: #FFFFFF;
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 400;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.5;
  cursor: pointer;
  appearance: none;
  transition: all 0.2s ease;
  min-height: 44px;
  
  &:hover {
    border-color: var(--border-dark);
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
  
  &:active {
    border-color: var(--primary-color);
  }
  
  option {
    padding: 0.5rem;
    font-weight: 400;
  }
`;

const DropdownArrow = styled.svg`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  pointer-events: none;
  color: var(--text-muted);
  transition: transform 0.15s ease, color 0.15s ease;
  
  ${FilterDropdownWrapper}:hover & {
    color: var(--text-secondary);
  }
  
  ${FilterSelect}:focus ~ & {
    color: var(--primary-color);
  }
`;

const StudentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const StudentCard = styled.div`
  background: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 20px;
  padding: 1.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 1.5rem;
  
  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    margin-bottom: 1.25rem;
  }
`;

const StudentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  cursor: pointer;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const StudentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-inverse);
  font-weight: 600;
  font-size: 1.125rem;
  flex-shrink: 0;
`;

const StudentDetails = styled.div`
  flex: 1;
`;

const StudentName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.25rem 0;
  letter-spacing: -0.01em;
  line-height: 1.4;
`;

const StudentEmail = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.5;
`;

const Statistics = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    width: 100%;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StatLabel = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
  font-weight: 500;
`;

const StatValue = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.02em;
  line-height: 1.2;
  
  &.average {
    color: var(--success-color);
  }
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: color 0.2s ease;
  
  &:hover {
    color: var(--primary-color);
  }
`;

// ============================================================================
// SECTION COMPONENTS
// ============================================================================

const GradesContainer = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
`;

const SectionCard = styled.div`
  background: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    margin-bottom: 1.25rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-color);
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SectionCount = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-secondary);
  background: var(--background-tertiary);
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  border: 1px solid var(--border-color);
`;

const GradesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

// ============================================================================
// GRADE ITEM COMPONENTS
// ============================================================================

const GradeItem = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 1.25rem;
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    padding: 1rem;
  }
`;

const GradeInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const StatusBadgeContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const GradeTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.75rem 0;
  letter-spacing: -0.01em;
  line-height: 1.4;
`;

const GradeMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.875rem 1.5rem;
  font-size: 0.8125rem;
  color: var(--text-muted);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.5;
  margin-top: 0.5rem;
  
  & > span {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0.25rem;
    min-width: 0; /* Allow flex items to shrink */
  }
  
  /* For assignment metadata, use fixed column widths for consistent alignment */
  &[data-assignment-meta] {
    grid-template-columns: minmax(180px, max-content) minmax(280px, 1fr);
    gap: 0.5rem 3.5rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    
    &[data-assignment-meta] {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
  }
`;

const MetaLabel = styled.span`
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 500;
  font-size: 0.8125rem; /* 13px */
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.025em;
  flex-shrink: 0; /* Prevent label from shrinking */
  
  &::after {
    content: ':';
    margin-right: 0.25rem;
  }
`;

const MetaValue = styled.span`
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 400;
  font-size: 0.9375rem; /* 15px */
  color: var(--text-primary);
  word-break: break-word; /* Allow long values to wrap */
  flex: 1;
  min-width: 0; /* Allow flex item to shrink */
`;

// ============================================================================
// STATUS BADGE COMPONENT
// ============================================================================

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.875rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  letter-spacing: 0.01em;
  
  &.on-time {
    background-color: rgba(104, 211, 145, 0.1);
    color: var(--success-dark);
    border: 1px solid var(--success-color);
  }
  
  &.late {
    background-color: rgba(246, 224, 94, 0.1);
    color: var(--warning-dark);
    border: 1px solid var(--warning-color);
  }
  
  &.not-submitted {
    background-color: rgba(252, 129, 129, 0.1);
    color: var(--error-color);
    border: 1px solid var(--error-color);
  }
  
  &.in-progress {
    background-color: rgba(96, 165, 250, 0.1);
    color: var(--info-color);
    border: 1px solid var(--info-color);
  }
`;

// ============================================================================
// ACTION BUTTONS
// ============================================================================

// Right-hand container that groups badge, score, and buttons together
const GradeItemRightSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.75rem;
  flex-shrink: 0;
  min-width: 0;
  
  @media (max-width: 768px) {
    align-items: stretch;
    width: 100%;
  }
`;

// Container for badge, score, and buttons in a horizontal row
const GradeItemActions = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 0.75rem;
  min-width: fit-content;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const GradeScore = styled.div`
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: -0.01em;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 100px;
  text-align: right;
  justify-content: flex-end;
  grid-column: 1;
  
  &.graded {
    color: var(--success-color);
  }
  
  &.pending {
    color: var(--warning-color);
  }
  
  &.not-started {
    color: var(--error-color);
  }
  
  @media (max-width: 768px) {
    justify-content: center;
    min-width: auto;
    grid-column: auto;
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: var(--background-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  &:hover {
    background: var(--background-secondary);
    border-color: var(--border-dark);
    color: var(--text-primary);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }
  
  svg {
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.875rem;
    font-size: 0.75rem;
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  cursor: pointer;
    color: var(--text-muted);
  flex-shrink: 0;
  
  &:hover {
    color: var(--primary-color);
  }
`;

// ============================================================================
// BULK ACTIONS
// ============================================================================

const BulkActionsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const BulkActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-radius: 12px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;
  
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  
  &:hover {
    background: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }
  
  &.secondary {
    background: var(--background-primary);
    color: var(--text-muted);
    border: 1px solid var(--border-color);
    
    &:hover {
      background: var(--background-secondary);
      border-color: var(--border-dark);
    }
  }
`;

// ============================================================================
// MODAL COMPONENTS (keeping existing structure)
// ============================================================================

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const Modal = styled.div`
  background: var(--background-primary);
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  letter-spacing: -0.02em;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--background-tertiary);
    color: var(--text-primary);
  }
  
  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', sans-serif;
  
  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border: none;
    
    &:hover {
      background: var(--primary-dark);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
    
    &:focus-visible {
      outline: 2px solid var(--primary-color);
      outline-offset: 2px;
    }
  }
  
  &.secondary {
    background: var(--background-primary);
    color: var(--text-muted);
    border: 1px solid var(--border-color);
    
    &:hover {
      background: var(--background-secondary);
      border-color: var(--border-dark);
    }
    
    &:focus-visible {
      outline: 2px solid var(--primary-color);
      outline-offset: 2px;
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ============================================================================
// COURSE GRADES & ATTENDANCE (keeping existing structure)
// ============================================================================

const CourseGradeSection = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 2px solid var(--border-color);
  margin-bottom: 2rem;
`;

const SummaryGrid = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 2px solid var(--border-color);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1.5rem;
  
  @media (max-width: 1024px) {
    flex-direction: column;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CourseGradeItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 120px 100px;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--background-secondary);
  border-radius: 12px;
  margin-bottom: 0.75rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const TabContent = styled.div`
  display: ${props => props.$active ? 'block' : 'none'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-muted);
  
  svg {
    width: 64px;
    height: 64px;
    margin: 0 auto 1rem;
    opacity: 0.5;
  }
  
  h3 {
    font-size: 1.25rem;
    margin: 0 0 0.5rem 0;
  }
  
  p {
    font-size: 0.875rem;
    margin: 0;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-muted);
  
  svg {
    width: 32px;
    height: 32px;
    margin: 0 auto 1rem;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function Gradebook() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [gradebookData, setGradebookData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'completed', 'pending-grade', 'not-submitted', 'pending-course-grade'
  const [sortBy, setSortBy] = useState('name');
  const [expandedStudents, setExpandedStudents] = useState(new Set());
  const [courses, setCourses] = useState([]);
  const [overrideModal, setOverrideModal] = useState(null); // { type: 'assignment'|'quiz'|'course', item: {...}, student: {...} }
  const [overrideScore, setOverrideScore] = useState('');
  const [overrideNumericGrade, setOverrideNumericGrade] = useState('');
  const [overrideNotes, setOverrideNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set()); // Set of item keys: "assignment-{studentId}-{assignmentId}" or "quiz-{studentId}-{assignmentId}"
  const [bulkGradeModal, setBulkGradeModal] = useState(false);
  const [bulkGradeScore, setBulkGradeScore] = useState('0');
  const [bulkGradeNotes, setBulkGradeNotes] = useState('');
  const [bulkGrading, setBulkGrading] = useState(false);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [selectedStudentAttendance, setSelectedStudentAttendance] = useState(null);
  const [submissionModalOpen, setSubmissionModalOpen] = useState(false);
  const [submissionModalData, setSubmissionModalData] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    // Initialize from URL parameter if present
    const tabParam = searchParams.get('tab');
    return tabParam && ['pending-grading', 'pending-final-grades', 'student-coursework', 'bulk-grade', 'create-rubric', 'edit-rubric'].includes(tabParam)
      ? tabParam
      : 'pending-grading';
  }); // 'pending-grading', 'pending-final-grades', 'student-coursework', 'bulk-grade', 'create-rubric', 'edit-rubric'
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [rubricBuilderOpen, setRubricBuilderOpen] = useState(false);
  const [editingRubricData, setEditingRubricData] = useState(null);
  const [refreshRubrics, setRefreshRubrics] = useState(false);
  const [availableRubrics, setAvailableRubrics] = useState([]);
  const [selectedRubricForCalculation, setSelectedRubricForCalculation] = useState(null);
  const [calculatedGradeData, setCalculatedGradeData] = useState(null);
  const [isCalculatingGrade, setIsCalculatingGrade] = useState(false);

  // External items state
  const [showExternalItemModal, setShowExternalItemModal] = useState(false);


  // Update URL when tab changes
  useEffect(() => {
    const currentTab = searchParams.get('tab');
    if (currentTab !== activeTab) {
      setSearchParams({ tab: activeTab });
    }
  }, [activeTab, searchParams, setSearchParams]);

  // Update active tab when URL parameter changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['pending-grading', 'pending-final-grades', 'student-coursework', 'bulk-grade', 'create-rubric', 'edit-rubric', 'add-external-item', 'external-items'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Restore scroll position and active tab when returning to the page
  useEffect(() => {
    const restoreScrollPosition = () => {
      if (!loading && filteredData.length > 0) {
        const savedScrollPosition = sessionStorage.getItem('gradebook-scroll-position');
        const savedActiveTab = sessionStorage.getItem('gradebook-active-tab');
        const urlTabParam = searchParams.get('tab');
        
        // Restore active tab if saved and no URL parameter (browser back button scenario)
        if (!urlTabParam && savedActiveTab && ['pending-grading', 'pending-final-grades', 'student-coursework', 'bulk-grade', 'create-rubric', 'edit-rubric', 'add-external-item', 'external-items'].includes(savedActiveTab)) {
          setActiveTab(savedActiveTab);
        }
        
        if (savedScrollPosition) {
          // Wait for DOM to render and tab to switch if needed, then restore scroll position
          const tabNeedsSwitch = !urlTabParam && savedActiveTab && savedActiveTab !== activeTab;
          setTimeout(() => {
            const scrollPosition = parseInt(savedScrollPosition, 10);
            window.scrollTo({
              top: scrollPosition,
              behavior: 'auto' // Use 'auto' for instant scroll, 'smooth' for animated
            });
            // Clear the saved position and tab after restoring
            sessionStorage.removeItem('gradebook-scroll-position');
            sessionStorage.removeItem('gradebook-active-tab');
          }, tabNeedsSwitch ? 200 : 100); // Longer delay if tab needs to switch
        } else if (savedActiveTab && !urlTabParam) {
          // Clear saved tab even if no scroll position
          sessionStorage.removeItem('gradebook-active-tab');
        }
      }
    };

    // Restore on mount or when data is ready
    restoreScrollPosition();

    // Also restore when page becomes visible again (handles browser back button)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        restoreScrollPosition();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loading, filteredData, activeTab, searchParams]);

  const fetchGradebook = useCallback(async () => {
    // CRITICAL FIX: Don't fetch if user doesn't exist (logged out)
    if (!user || (user.role !== 'instructor' && user.role !== 'admin')) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      const courseId = courseFilter === 'all' ? null : courseFilter;
      const response = await api.getGradebook(courseId);
      
      // CRITICAL FIX: Check if user still exists (might have logged out during fetch)
      if (!user) {
        return; // User logged out, don't process response
      }
      
      if (response?.success) {
        const data = response.data || [];
        // Remove console.log in production - only log in development
        if (process.env.NODE_ENV === 'development') {
          console.log('Gradebook data received:', data);
          console.log('First student stats:', data[0]?.statistics);
        }
        setGradebookData(data);
      } else {
        setError(response.error || 'Failed to fetch gradebook data');
      }
    } catch (err) {
      // Don't log errors if user has logged out (401 is expected)
      const isAuthError = err?.status === 401 || 
                         err?.message?.includes('Token revoked') ||
                         err?.message?.includes('session has expired') ||
                         err?.message?.includes('logged out');
      
      if (!isAuthError) {
        console.error('Error fetching gradebook:', err);
        setError('Failed to fetch gradebook data');
      }
      // If auth error and user doesn't exist, don't set error (logout is in progress)
      if (isAuthError && !user) {
        return; // User logged out, don't set error state
      }
      if (!isAuthError) {
        setError('Failed to fetch gradebook data');
      }
    } finally {
      setLoading(false);
    }
  }, [courseFilter, user]);

  useEffect(() => {
    if (user?.role === 'instructor' || user?.role === 'admin') {
      fetchGradebook();
    }
  }, [user, fetchGradebook]);

  // Fetch courses for filter
  useEffect(() => {
    const fetchCourses = async () => {
      // CRITICAL FIX: Don't fetch if user doesn't exist (logged out)
      if (!user || (user.role !== 'instructor' && user.role !== 'admin')) {
        return;
      }
      
      try {
        const response = await api.getCourses();
        if (response?.success) {
          // The API returns { success: true, courses: [...] } for authenticated users
          // and { success: true, data: [...] } for some other endpoints
          const coursesData = response.courses || response.data || [];
          const coursesArray = Array.isArray(coursesData) ? coursesData : [];
          // Remove console.log in production - only log in development
          if (process.env.NODE_ENV === 'development') {
            console.log('Fetched courses for filter:', {
              count: coursesArray.length,
              courses: coursesArray,
              userRole: user?.role,
              responseKeys: Object.keys(response)
            });
          }
          
          // Ensure we have valid course objects with id and name
          const validCourses = coursesArray.filter(course => course && course.id && course.name);
          // Remove console.log in production - only log in development
          if (process.env.NODE_ENV === 'development') {
            console.log('Valid courses after filtering:', validCourses.length);
          }
          setCourses(validCourses);
        } else {
          // Don't log errors if user has logged out (401 is expected)
          const isAuthError = response?.status === 401 || 
                             response?.error?.includes('Token revoked') ||
                             response?.error?.includes('session has expired');
          if (!isAuthError) {
            console.error('Failed to fetch courses:', response.error || response.message);
          }
        }
      } catch (err) {
        // Don't log errors if user has logged out (401 is expected)
        const isAuthError = err?.status === 401 || 
                           err?.message?.includes('Token revoked') ||
                           err?.message?.includes('session has expired') ||
                           err?.message?.includes('logged out');
        if (!isAuthError) {
          console.error('Error fetching courses:', err);
          setError('Failed to load courses for filtering');
        }
        // If auth error and user doesn't exist, don't set error (logout is in progress)
        if (isAuthError && !user) {
          return; // User logged out, don't set error state
        }
      }
    };
    if (user?.role === 'instructor' || user?.role === 'admin') {
      fetchCourses();
    }
  }, [user]);

  // Filter and sort data
  useEffect(() => {
    let filtered = [...gradebookData];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(student => {
        const firstName = student.first_name?.toLowerCase() || '';
        const lastName = student.last_name?.toLowerCase() || '';
        const fullName = student.name?.toLowerCase() || `${firstName} ${lastName}`.trim();
        const email = student.email?.toLowerCase() || '';
        const combinedName = `${firstName} ${lastName}`.trim();
        
        return (
          firstName.includes(searchLower) ||
          lastName.includes(searchLower) ||
          email.includes(searchLower) ||
          combinedName.includes(searchLower) ||
          fullName.includes(searchLower)
        );
      });
    }

    // Apply course filter (server-side filtering + client-side safety filter)
    // Filter assignments and quizzes by course_id/course_code to ensure only items from selected course are shown
    filtered = filtered.map(student => {
      const filteredStudent = { ...student };
      
      // Filter assignments by course
      if (courseFilter && courseFilter !== 'all') {
        // Find the course to get its ID and code
        const selectedCourse = courses.find(c => c.id === parseInt(courseFilter) || c.code === courseFilter);
        if (selectedCourse) {
          filteredStudent.assignments = student.assignments?.filter(a => {
            // Match by course_id or course_code
            const matchesCourseId = a.course_id && parseInt(a.course_id) === selectedCourse.id;
            const matchesCourseCode = a.course_code && a.course_code === selectedCourse.code;
            return matchesCourseId || matchesCourseCode;
          }) || [];
          
          filteredStudent.quizzes = student.quizzes?.filter(q => {
            // Match by course_id or course_code
            const matchesCourseId = q.course_id && parseInt(q.course_id) === selectedCourse.id;
            const matchesCourseCode = q.course_code && q.course_code === selectedCourse.code;
            return matchesCourseId || matchesCourseCode;
          }) || [];
        } else {
          // If course not found, filter by courseFilter directly (might be course ID or code)
          filteredStudent.assignments = student.assignments?.filter(a => {
            const matchesCourseId = a.course_id && parseInt(a.course_id) === parseInt(courseFilter);
            const matchesCourseCode = a.course_code && a.course_code === courseFilter;
            return matchesCourseId || matchesCourseCode;
          }) || [];
          
          filteredStudent.quizzes = student.quizzes?.filter(q => {
            const matchesCourseId = q.course_id && parseInt(q.course_id) === parseInt(courseFilter);
            const matchesCourseCode = q.course_code && q.course_code === courseFilter;
            return matchesCourseId || matchesCourseCode;
          }) || [];
        }
      } else {
        // No course filter - show all assignments/quizzes
        filteredStudent.assignments = student.assignments || [];
        filteredStudent.quizzes = student.quizzes || [];
      }
      
      // Apply status filter for assignments/quizzes
      if (statusFilter === 'completed') {
        filteredStudent.assignments = filteredStudent.assignments.filter(a => 
          a.status === 'graded' || a.submitted_at
        );
        filteredStudent.quizzes = filteredStudent.quizzes.filter(q => 
          q.submitted_at && q.score !== null
        );
      } else if (statusFilter === 'pending-grade') {
        // Show only submitted but not graded items
        filteredStudent.assignments = filteredStudent.assignments.filter(a => 
          a.submitted_at && a.status !== 'graded'
        );
        filteredStudent.quizzes = filteredStudent.quizzes.filter(q => 
          q.submitted_at && (q.score === null || q.score === undefined)
        );
      } else if (statusFilter === 'not-submitted') {
        // Show only not submitted items
        filteredStudent.assignments = filteredStudent.assignments.filter(a => 
          !a.submitted_at
        );
        filteredStudent.quizzes = filteredStudent.quizzes.filter(q => !q.submitted_at);
      }

      return filteredStudent;
    });

    // Apply pending course grade filter (filters students, not assignments/quizzes)
    if (statusFilter === 'pending-course-grade') {
      filtered = filtered.filter(student => {
        // Show only students who have at least one course without a final grade assigned
        return student.courseGrades?.some(courseGrade => 
          !courseGrade.grade || courseGrade.grade === null || courseGrade.grade === undefined
        ) || false;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim();
          const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim();
          return nameA.localeCompare(nameB);
        case 'average-desc':
          return (b.statistics?.averageScore || 0) - (a.statistics?.averageScore || 0);
        case 'average-asc':
          return (a.statistics?.averageScore || 0) - (b.statistics?.averageScore || 0);
        case 'total-desc':
          return (b.statistics?.totalItems || 0) - (a.statistics?.totalItems || 0);
        case 'total-asc':
          return (a.statistics?.totalItems || 0) - (b.statistics?.totalItems || 0);
        default:
          return 0;
      }
    });

    setFilteredData(filtered);
  }, [gradebookData, searchTerm, statusFilter, sortBy, courseFilter, courses]);

  // Load available rubrics when course grade modal is opened
  useEffect(() => {
    if (overrideModal?.type === 'course' && overrideModal.item?.course_id) {
      const fetchRubrics = async () => {
        try {
          const response = await api.request(`/rubrics/course/${overrideModal.item.course_id}`);
          if (response.success && response.data) {
            setAvailableRubrics(response.data);
            
            // If there's only one rubric, automatically select and calculate it
            if (response.data.length === 1) {
              const singleRubric = response.data[0];
              setSelectedRubricForCalculation(singleRubric.id);
              
              // Automatically calculate the grade
              if (overrideModal?.student?.id) {
                setIsCalculatingGrade(true);
                try {
                  const calcResponse = await api.request(
                    `/final-grades/students/${overrideModal.student.id}/courses/${overrideModal.item.course_id}?rubricId=${singleRubric.id}`
                  );
                  
                  if (calcResponse.success && calcResponse.data) {
                    setCalculatedGradeData(calcResponse.data);
                    // Auto-populate the grade field with calculated grade
                    setOverrideScore(calcResponse.data.letterGrade || '');
                    setOverrideNumericGrade(Math.round(calcResponse.data.finalPercentage).toString());
                  }
                } catch (err) {
                  console.error('Error auto-calculating grade:', err);
                } finally {
                  setIsCalculatingGrade(false);
                }
              }
            } else {
              // Reset calculation state when modal opens with multiple rubrics
              setSelectedRubricForCalculation(null);
              setCalculatedGradeData(null);
            }
          }
        } catch (err) {
          console.error('Error fetching rubrics:', err);
        }
      };
      fetchRubrics();
    } else if (!overrideModal) {
      // Clear rubric data when modal closes
      setAvailableRubrics([]);
      setSelectedRubricForCalculation(null);
      setCalculatedGradeData(null);
    }
  }, [overrideModal]);

  const toggleStudent = (studentId) => {
    const newExpanded = new Set(expandedStudents);
    if (newExpanded.has(studentId)) {
      newExpanded.delete(studentId);
    } else {
      newExpanded.add(studentId);
    }
    setExpandedStudents(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTimestamp = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatScore = (score, maxScore) => {
    if (score === null || score === undefined || maxScore === null || maxScore === undefined) {
      return 'Pending';
    }
    return `${Number(score).toFixed(1)} / ${Number(maxScore).toFixed(1)}`;
  };

  const handleOverride = (type, item, student) => {
    if (type === 'assignment') {
      setOverrideScore(item.grade || '');
      setOverrideNotes('');
    } else if (type === 'quiz') {
      setOverrideScore(item.score || '');
      setOverrideNotes('');
    } else if (type === 'course') {
      const courseGrade = student.courseGrades?.find(cg => cg.course_id === item.course_id);
      setOverrideScore(courseGrade?.grade || '');
      setOverrideNumericGrade(courseGrade?.numeric_grade || '');
      setOverrideNotes(courseGrade?.grade_notes || '');
    }
    setOverrideModal({ type, item, student });
  };

  const getItemKey = (type, studentId, item) => {
    return `${type}-${studentId}-${item.assignment_id || item.id || item.quiz_id}`;
  };

  const toggleItemSelection = (type, studentId, item) => {
    const key = getItemKey(type, studentId, item);
    const newSelected = new Set(selectedItems);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedItems(newSelected);
  };

  const selectAllNotStarted = () => {
    const newSelected = new Set();
    filteredData.forEach(student => {
      student.assignments?.forEach(a => {
        if (!a.submitted_at && a.status !== 'graded') {
          newSelected.add(getItemKey('assignment', student.id, a));
        }
      });
      student.quizzes?.forEach(q => {
        if (!q.submitted_at) {
          newSelected.add(getItemKey('quiz', student.id, q));
        }
      });
    });
    setSelectedItems(newSelected);
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  const handleBulkGrade = async () => {
    const score = parseFloat(bulkGradeScore);
    if (isNaN(score) || score < 0) {
      alert('Please enter a valid score');
      return;
    }

    setBulkGrading(true);
      const results = { success: 0, failed: 0, errors: [] };
      
    try {
      for (const key of selectedItems) {
        const [type, studentId, itemId] = key.split('-');
        const student = filteredData.find(s => s.id === parseInt(studentId));
        if (!student) continue;

        const item = type === 'assignment' 
          ? student.assignments?.find(a => (a.assignment_id || a.id)?.toString() === itemId)
          : student.quizzes?.find(q => (q.assignment_id || q.id)?.toString() === itemId);

        if (!item) continue;

        try {
          if (type === 'assignment') {
            if (item.id) {
              await api.overrideAssignmentGrade(
                item.assignment_id,
                item.id,
                score,
                bulkGradeNotes,
                true,
                bulkGradeNotes
              );
            } else {
              await api.overrideAssignmentGrade(
                item.assignment_id,
                null,
                score,
                bulkGradeNotes,
                true,
                bulkGradeNotes
              );
            }
          } else {
            // For quizzes, item.id is the attempt_id
            // Only grade quizzes that have been submitted (have an attempt_id)
            if (!item.id) {
              results.errors.push(`${item.title}: Cannot grade quiz that has not been submitted`);
              continue;
            }
            
            await api.overrideQuizGrade(
                item.assignment_id,
                item.id,
                score,
                bulkGradeNotes
              );
            }
            results.success++;
        } catch (err) {
          results.failed++;
          results.errors.push(`${item.title}: ${err.message || 'Unknown error'}`);
        }
      }

      setBulkGradeModal(false);
      setBulkGradeScore('0');
      setBulkGradeNotes('');
      setSelectedItems(new Set());
      
      if (results.failed > 0) {
        alert(`Graded ${results.success} items successfully. ${results.failed} failed.\n\nErrors:\n${results.errors.join('\n')}`);
      } else {
        setSuccessMessage(`Successfully graded ${results.success} item${results.success !== 1 ? 's' : ''}`);
        setSuccessModalOpen(true);
      }
      
      await fetchGradebook();
    } catch (err) {
      console.error('Error in bulk grading:', err);
      alert('An error occurred during bulk grading');
    } finally {
      setBulkGrading(false);
    }
  };

  const handleViewResults = (type, item, student = null, fromPendingGrading = false) => {
    try {
      // Open modal instead of navigating
      if (student) {
        setSubmissionModalData({
          type,
          item,
          student
        });
        setSubmissionModalOpen(true);
      } else {
        // Fallback to navigation if no student data
        if (type === 'assignment') {
          navigate(`/assignments/${item.assignment_id}/submissions`);
        } else if (type === 'quiz') {
          if (item.id) {
            navigate(`/instructor/submissions/${item.id}`);
          } else {
            navigate(`/instructor/quizzes/${item.quiz_id}/attempts`);
          }
        }
      }
    } catch (err) {
      console.error('Error opening submission view:', err);
    }
  };

  const handleCalculateGrade = async () => {
    if (!selectedRubricForCalculation || !overrideModal?.item?.course_id || !overrideModal?.student?.id) {
      alert('Please select a rubric');
      return;
    }

    setIsCalculatingGrade(true);
    try {
      const response = await api.request(
        `/final-grades/students/${overrideModal.student.id}/courses/${overrideModal.item.course_id}?rubricId=${selectedRubricForCalculation}`
      );

      if (response.success && response.data) {
        setCalculatedGradeData(response.data);
        // Auto-populate the grade field with calculated grade
        setOverrideScore(response.data.letterGrade || '');
        setOverrideNumericGrade(Math.round(response.data.finalPercentage).toString());
      } else {
        alert(response.error || 'Failed to calculate grade');
      }
    } catch (err) {
      alert(`Error calculating grade: ${err.message}`);
      console.error('Error calculating grade:', err);
    } finally {
      setIsCalculatingGrade(false);
    }
  };

  const handleSaveOverride = async () => {
    if (!overrideModal) return;

    setSaving(true);
    try {
      let score = overrideScore.trim();
      
      // For course grades, allow both letter and numeric grades
      if (overrideModal.type === 'course') {
        // Grade is required
        if (!score || score.trim() === '') {
          alert('Please enter a grade (letter or number)');
          setSaving(false);
          return;
        }
        // Validate letter grade (A-F with optional +/-) or numeric grade (0-100)
        const letterGradePattern = /^[A-F][+-]?$/i;
        const numericValue = parseFloat(score);
        
        if (!letterGradePattern.test(score) && (isNaN(numericValue) || numericValue < 0 || numericValue > 100)) {
          alert('Please enter a valid grade: letter grade (A-F with optional +/-) or number (0-100)');
          setSaving(false);
          return;
        }
        // Keep score as string for course grades (can be letter or number)
      } else {
        // For assignments and quizzes, only numeric grades are allowed
        const numericScore = parseFloat(score);
        if (isNaN(numericScore) || numericScore < 0) {
          alert('Please enter a valid score');
          setSaving(false);
          return;
        }
        score = numericScore;
      }

      if (overrideModal.type === 'assignment') {
          await api.overrideAssignmentGrade(
            overrideModal.item.assignment_id,
            overrideModal.item.id,
            score,
            overrideNotes
          );
      } else if (overrideModal.type === 'quiz') {
        // For quizzes, item.id is the attempt_id (from backend mapping)
        // If attempt_id is null, the quiz hasn't been submitted yet and cannot be graded
        const attemptId = overrideModal.item.id; // This is attempt_id from backend
        
        if (!attemptId) {
          alert('Cannot grade quiz: Quiz must be submitted before it can be graded');
          setSaving(false);
          return;
        }
        
        await api.overrideQuizGrade(
            overrideModal.item.assignment_id,
          attemptId,
            score,
            overrideNotes
          );
      } else if (overrideModal.type === 'course') {
        const numericGrade = overrideNumericGrade.trim() ? parseFloat(overrideNumericGrade.trim()) : null;
        if (numericGrade !== null && (isNaN(numericGrade) || numericGrade < 0 || numericGrade > 100)) {
          alert('Please enter a valid numeric grade (0-100) or leave it empty');
          setSaving(false);
          return;
        }
        await api.assignCourseGrade(
          overrideModal.item.course_id,
          overrideModal.student.id,
          score,
          overrideNotes,
          numericGrade
        );
      }

      setOverrideModal(null);
      setOverrideScore('');
      setOverrideNumericGrade('');
      setOverrideNotes('');
      await fetchGradebook(); // Refresh data
    } catch (err) {
      console.error('Error saving override:', err);
      let errorMessage = 'Failed to save grade override';
      
      // Try to extract detailed error message
      if (err.message) {
        errorMessage = err.message;
      } else if (err.error) {
        errorMessage = err.error;
      } else if (err.details && Array.isArray(err.details)) {
        // Handle validation errors from backend
        errorMessage = err.details.map(d => d.msg || d.message).join(', ');
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      
      alert(`Failed to save grade override: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  // Compute courseId from courseFilter for use in component
  const courseId = courseFilter === 'all' ? null : courseFilter;
  const isBulkGradeActive = activeTab === 'bulk-grade';
  const closeBulkGrade = () => setActiveTab('pending-grading');


  if (loading) {
    return (
      <PageWrapper>
        <Container>
          <LoadingState>
            <FiClock />
            <p>Loading gradebook...</p>
          </LoadingState>
        </Container>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <Container>
          <EmptyState>
            <FiAlertCircle />
            <h3>Error</h3>
            <p>{error}</p>
          </EmptyState>
        </Container>
      </PageWrapper>
    );
  }

  // Calculate counts for tabs
  const pendingGradingCount = (() => {
    let count = 0;
    filteredData.forEach(student => {
      student.assignments?.forEach(assignment => {
        if (assignment.submitted_at && assignment.status !== 'graded') count++;
      });
      student.quizzes?.forEach(quiz => {
        if (quiz.submitted_at && (quiz.score === null || quiz.score === undefined)) count++;
      });
    });
    return count;
  })();

  const pendingFinalGradesCount = (() => {
    let count = 0;
    filteredData.forEach(student => {
      student.courseGrades?.forEach(courseGrade => {
        if (!courseGrade.grade || courseGrade.grade === null || courseGrade.grade === undefined) count++;
      });
    });
    return count;
  })();

  return (
    <PageWrapper>
      <MobileNavSelect
        value={activeTab}
        onChange={(e) => {
          const newTab = e.target.value;
          setActiveTab(newTab);
          if (newTab === 'add-external-item' && courseId) {
            setShowExternalItemModal(true);
          }
        }}
        aria-label="Select gradebook section"
      >
        <option value="pending-grading">Pending Assignment Grades {pendingGradingCount > 0 ? `(${pendingGradingCount})` : ''}</option>
        <option value="pending-final-grades">Pending Course Grades {pendingFinalGradesCount > 0 ? `(${pendingFinalGradesCount})` : ''}</option>
        <option value="student-coursework">Student Coursework</option>
        <option value="bulk-grade">Bulk Grade</option>
        <option value="create-rubric">Create Rubric</option>
        <option value="edit-rubric">Edit Rubric</option>
        <option value="add-external-item">Add External Item</option>
        <option value="external-items">External Items</option>
      </MobileNavSelect>

      <DashboardContainer>
        <Sidebar>
          <SidebarPanel>
            <SidebarLabel>GRADEBOOK SECTIONS</SidebarLabel>
            <SidebarNav aria-label="Gradebook sections">
              <NavList>
                <NavListItem>
                  <NavItem 
                    $active={activeTab === 'pending-grading'} 
                    onClick={() => setActiveTab('pending-grading')}
                    aria-current={activeTab === 'pending-grading' ? 'page' : undefined}
                  >
                    <FiClock />
                    <NavLabel>Pending Assignment Grades</NavLabel>
                    {pendingGradingCount > 0 && (
                      <NavBadge $active={activeTab === 'pending-grading'}>
                        {pendingGradingCount}
                      </NavBadge>
                    )}
                  </NavItem>
                </NavListItem>
                <NavListItem>
                  <NavItem 
                    $active={activeTab === 'pending-final-grades'} 
                    onClick={() => setActiveTab('pending-final-grades')}
                    aria-current={activeTab === 'pending-final-grades' ? 'page' : undefined}
                  >
                    <FiCheckCircle />
                    <NavLabel>Pending Course Grades</NavLabel>
                    {pendingFinalGradesCount > 0 && (
                      <NavBadge $active={activeTab === 'pending-final-grades'}>
                        {pendingFinalGradesCount}
                      </NavBadge>
                    )}
                  </NavItem>
                </NavListItem>
                <NavListItem>
                  <NavItem
                    $active={activeTab === 'student-coursework'}
                    onClick={() => setActiveTab('student-coursework')}
                    aria-current={activeTab === 'student-coursework' ? 'page' : undefined}
                  >
                    <FiUser />
                    <NavLabel>Student Coursework</NavLabel>
                  </NavItem>
                </NavListItem>
                <NavListItem>
                  <NavItem
                    $active={activeTab === 'bulk-grade'}
                    onClick={() => setActiveTab('bulk-grade')}
                    aria-current={activeTab === 'bulk-grade' ? 'page' : undefined}
                  >
                    <FiUsers />
                    <NavLabel>Bulk Rubric Grading</NavLabel>
                  </NavItem>
                </NavListItem>
                <NavListItem>
                  <NavItem
                    $active={activeTab === 'create-rubric'}
                    onClick={() => setActiveTab('create-rubric')}
                    aria-current={activeTab === 'create-rubric' ? 'page' : undefined}
                  >
                    <FiEdit3 />
                    <NavLabel>Create Grading Rubric</NavLabel>
                  </NavItem>
                </NavListItem>
                <NavListItem>
                  <NavItem
                    $active={activeTab === 'edit-rubric'}
                    onClick={() => setActiveTab('edit-rubric')}
                    aria-current={activeTab === 'edit-rubric' ? 'page' : undefined}
                  >
                    <FiEdit3 />
                    <NavLabel>Edit Grading Rubric</NavLabel>
                  </NavItem>
                </NavListItem>
                <NavListItem>
                  <NavItem
                    $active={activeTab === 'add-external-item'}
                    onClick={() => {
                      if (courseId) {
                        setActiveTab('add-external-item');
                        setShowExternalItemModal(true);
                      }
                    }}
                    aria-current={activeTab === 'add-external-item' ? 'page' : undefined}
                  >
                    <FiPlus />
                    <NavLabel>Add External Item</NavLabel>
                  </NavItem>
                </NavListItem>
                <NavListItem>
                  <NavItem
                    $active={activeTab === 'external-items'}
                    onClick={() => setActiveTab('external-items')}
                    aria-current={activeTab === 'external-items' ? 'page' : undefined}
                  >
                    <FiPackage />
                    <NavLabel>External Items</NavLabel>
                  </NavItem>
                </NavListItem>
              </NavList>
            </SidebarNav>
          </SidebarPanel>
        </Sidebar>

        <MainContent>
          <Container>
          <Header>
            <Title>Gradebook</Title>
            <Subtitle>Manage student grades, assignments, and course performance</Subtitle>
          </Header>

          {/* Stats Cards */}
          <StatsGrid>
            <StatCard>
              <StatCardHeader>
                <StatCardTitle>Pending Assignments</StatCardTitle>
                <StatCardIcon $color="rgba(255, 193, 7, 0.1)" $iconColor="#ffc107">
                  <FiClock />
                </StatCardIcon>
              </StatCardHeader>
              <StatCardValue>{pendingGradingCount}</StatCardValue>
              <StatCardSubtext>Require grading</StatCardSubtext>
            </StatCard>
            <StatCard>
              <StatCardHeader>
                <StatCardTitle>Pending Course Grades</StatCardTitle>
                <StatCardIcon $color="rgba(33, 150, 243, 0.1)" $iconColor="#2196f3">
                  <FiCheckCircle />
                </StatCardIcon>
              </StatCardHeader>
              <StatCardValue>{pendingFinalGradesCount}</StatCardValue>
              <StatCardSubtext>Need final grades</StatCardSubtext>
            </StatCard>
            <StatCard>
              <StatCardHeader>
                <StatCardTitle>Total Students</StatCardTitle>
                <StatCardIcon $color="rgba(76, 175, 80, 0.1)" $iconColor="#4caf50">
                  <FiUsers />
                </StatCardIcon>
              </StatCardHeader>
              <StatCardValue>{filteredData.length}</StatCardValue>
              <StatCardSubtext>Enrolled students</StatCardSubtext>
            </StatCard>
          </StatsGrid>

          <ToolbarCard>
            <ToolbarGrid>
              {/* Row 1: Search (takes most width) + Sort (right) */}
              <SearchInput
                type="text"
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FilterDropdownWrapper>
                <FilterSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="name">Sort by Name</option>
                  <option value="average-desc">Average: High to Low</option>
                  <option value="average-asc">Average: Low to High</option>
                  <option value="total-desc">Total Items: High to Low</option>
                  <option value="total-asc">Total Items: Low to High</option>
                </FilterSelect>
                <DropdownArrow viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </DropdownArrow>
              </FilterDropdownWrapper>
              
              {/* Row 2: Course + Status */}
              <FilterDropdownWrapper>
                <FilterSelect value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}>
                  <option value="all">All Courses</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>{course.name}</option>
                  ))}
                </FilterSelect>
                <DropdownArrow viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </DropdownArrow>
              </FilterDropdownWrapper>
              <FilterDropdownWrapper>
                <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="pending-grade">Pending Assignment Grade</option>
                  <option value="pending-course-grade">Pending Course Grade</option>
                  <option value="not-submitted">Not Submitted</option>
                </FilterSelect>
                <DropdownArrow viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </DropdownArrow>
              </FilterDropdownWrapper>
            </ToolbarGrid>
          </ToolbarCard>

        {/* Pending Assignment Grades Tab */}
        <TabContent $active={activeTab === 'pending-grading'}>
          {(() => {
            // Collect all pending items (submitted but not graded) from all students
            const pendingItems = [];
        
        filteredData.forEach(student => {
          // Pending assignments: submitted but not graded
          student.assignments?.forEach(assignment => {
            if (assignment.submitted_at && assignment.status !== 'graded') {
              pendingItems.push({
                type: 'assignment',
                item: assignment,
                student: {
                  id: student.id,
                  name: `${student.first_name} ${student.last_name}`,
                  email: student.email
                }
              });
            }
          });
          
          // Pending quizzes: submitted but not graded
          student.quizzes?.forEach(quiz => {
            if (quiz.submitted_at && (quiz.score === null || quiz.score === undefined)) {
              pendingItems.push({
                type: 'quiz',
                item: quiz,
                student: {
                  id: student.id,
                  name: `${student.first_name} ${student.last_name}`,
                  email: student.email
                }
              });
            }
          });
        });
        
        if (pendingItems.length > 0) {
          return (
            <SectionCard style={{ marginBottom: '2rem', background: 'rgba(246, 224, 94, 0.1)', borderColor: 'var(--warning-color)' }}>
              <SectionHeader>
                <SectionTitle>
                  <FiClock size={20} />
                  Pending Assignment Grades
                </SectionTitle>
                <SectionCount>{pendingItems.length}</SectionCount>
              </SectionHeader>
              <GradesList>
                {pendingItems.map((pendingItem, index) => {
                  const { type, item, student: studentInfo } = pendingItem;
                  const isLate = item.submitted_at && item.due_date 
                    ? new Date(item.submitted_at) > new Date(item.due_date)
                    : false;
                  
                  return (
                    <GradeItem key={`${type}-${studentInfo.id}-${item.assignment_id || item.id}-${index}`}>
                      <GradeInfo>
                        <GradeTitle>{item.title}</GradeTitle>
                        <GradeMeta style={{ gridTemplateColumns: '1fr 1fr', gap: '0.5rem 2rem' }}>
                          {/* Left Column */}
                          <span style={{ gridColumn: '1', gridRow: '1' }}>
                            <MetaLabel>Student</MetaLabel>
                            <MetaValue style={{ fontWeight: 600 }}>{studentInfo.name}</MetaValue>
                          </span>
                          <span style={{ gridColumn: '1', gridRow: '2' }}>
                            <MetaLabel>Course</MetaLabel>
                            <MetaValue>{item.course_code || 'N/A'}</MetaValue>
                          </span>
                          {/* Right Column */}
                          {item.submitted_at && (
                            <span style={{ gridColumn: '2', gridRow: '1' }}>
                              <MetaLabel>Submitted</MetaLabel>
                              <MetaValue>{formatTimestamp(item.submitted_at)}</MetaValue>
                            </span>
                          )}
                          <span style={{ gridColumn: '2', gridRow: '2' }}>
                            <MetaLabel>Due</MetaLabel>
                            <MetaValue>{item.due_date ? formatTimestamp(item.due_date) : 'Not Specified'}</MetaValue>
                          </span>
                          {(type === 'assignment' && item.posted_date) || (type === 'quiz' && item.assigned_at) ? (
                            <span style={{ gridColumn: '2', gridRow: '3' }}>
                              <MetaLabel>{type === 'assignment' ? 'Posted' : 'Assigned'}</MetaLabel>
                              <MetaValue>{type === 'assignment' ? formatTimestamp(item.posted_date) : formatTimestamp(item.assigned_at)}</MetaValue>
                            </span>
                          ) : null}
                        </GradeMeta>
                      </GradeInfo>
                      <GradeItemRightSection>
                        {item.submitted_at && (
                          <StatusBadgeContainer>
                            <StatusBadge className={isLate ? 'late' : 'on-time'}>
                              {isLate ? 'Late' : 'On Time'}
                            </StatusBadge>
                          </StatusBadgeContainer>
                        )}
                        <GradeItemActions>
                          <GradeScore className="pending" style={{ color: 'var(--warning-color)' }}>
                            Pending
                          </GradeScore>
                          {type === 'assignment' && item.id && (
                            <ActionButton onClick={() => handleViewResults('assignment', item, studentInfo, true)}>
                              <FiEye size={16} />
                              View
                            </ActionButton>
                          )}
                          {type === 'quiz' && item.id && (
                            <ActionButton onClick={() => handleViewResults('quiz', item, studentInfo, true)}>
                              <FiEye size={16} />
                              View
                            </ActionButton>
                          )}
                          <ActionButton 
                            onClick={() => {
                              const student = filteredData.find(s => s.id === studentInfo.id);
                              if (student) {
                                handleOverride(type, item, student);
                              }
                            }}
                            disabled={!item.submitted_at}
                            style={!item.submitted_at ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                          >
                            <FiEdit3 size={16} />
                            Grade
                          </ActionButton>
                        </GradeItemActions>
                      </GradeItemRightSection>
                    </GradeItem>
                  );
                })}
              </GradesList>
            </SectionCard>
          );
        }
            if (pendingItems.length === 0) {
              return (
                <EmptyState>
                  <FiClock size={48} />
                  <h3>No Pending Assignment Grades</h3>
                  <p>All submitted assignments and quizzes have been graded.</p>
                </EmptyState>
              );
            }
            return null;
          })()}
        </TabContent>

        {/* Pending Course Grades Tab */}
        <TabContent $active={activeTab === 'pending-final-grades'}>
          {(() => {
            // Collect all pending course final grades from all students
            const pendingFinalGrades = [];
        
        filteredData.forEach(student => {
          student.courseGrades?.forEach(courseGrade => {
            if (!courseGrade.grade || courseGrade.grade === null || courseGrade.grade === undefined) {
              pendingFinalGrades.push({
                courseGrade: courseGrade,
                student: {
                  id: student.id,
                  name: `${student.first_name} ${student.last_name}`,
                  email: student.email
                }
              });
            }
          });
        });
        
        if (pendingFinalGrades.length > 0) {
          return (
            <SectionCard style={{ marginBottom: '2rem', background: 'rgba(96, 165, 250, 0.1)', borderColor: 'var(--info-color)' }}>
              <SectionHeader>
                <SectionTitle>
                  <FiCheckCircle size={20} />
                  Pending Course Grades
                </SectionTitle>
                <SectionCount>{pendingFinalGrades.length}</SectionCount>
              </SectionHeader>
              <GradesList>
                {pendingFinalGrades.map((pendingItem, index) => {
                  const { courseGrade, student: studentInfo } = pendingItem;
                  
                  return (
                    <GradeItem key={`course-grade-${studentInfo.id}-${courseGrade.course_id}-${index}`}>
                      <GradeInfo>
                        <GradeTitle>
                          {courseGrade.course_name || 'N/A'} ({courseGrade.course_code || 'N/A'})
                        </GradeTitle>
                        <GradeMeta>
                          <span>
                            <MetaLabel>Student</MetaLabel>
                            <MetaValue style={{ fontWeight: 600 }}>{studentInfo.name}</MetaValue>
                          </span>
                          <span>
                            <MetaLabel>Email</MetaLabel>
                            <MetaValue>{studentInfo.email}</MetaValue>
                          </span>
                        </GradeMeta>
                      </GradeInfo>
                      <GradeItemRightSection>
                        <GradeItemActions>
                          <GradeScore className="pending" style={{ color: 'var(--info-color)' }}>
                            Pending
                          </GradeScore>
                          <ActionButton onClick={() => {
                            const student = filteredData.find(s => s.id === studentInfo.id);
                            if (student) {
                              handleOverride('course', { 
                                course_id: courseGrade.course_id,
                                course_name: courseGrade.course_name,
                                course_code: courseGrade.course_code
                              }, student);
                            }
                          }}>
                            <FiEdit3 size={16} />
                            Assign
                          </ActionButton>
                        </GradeItemActions>
                      </GradeItemRightSection>
                    </GradeItem>
                  );
                })}
              </GradesList>
            </SectionCard>
          );
        }
            if (pendingFinalGrades.length === 0) {
              return (
                <EmptyState>
                  <FiCheckCircle size={48} />
                  <h3>No Pending Course Grades</h3>
                  <p>All course final grades have been assigned.</p>
                </EmptyState>
              );
            }
            return null;
          })()}
        </TabContent>

        {/* Student Coursework & Attendance Tab */}
        <TabContent $active={activeTab === 'student-coursework'}>
      {filteredData.length === 0 ? (
        <EmptyState>
          <FiUser />
          <h3>No students found</h3>
          <p>Try adjusting your search or filter criteria</p>
        </EmptyState>
      ) : (
        <StudentsList>
          {filteredData.map((student) => {
            const isExpanded = expandedStudents.has(student.id);
            const stats = student.statistics || {};
            const initials = `${student.first_name?.[0] || ''}${student.last_name?.[0] || ''}`.toUpperCase();
            
            return (
              <StudentCard key={student.id}>
                <StudentHeader onClick={() => toggleStudent(student.id)}>
                  <StudentInfo>
                    <Avatar>{initials}</Avatar>
                    <StudentDetails>
                      <StudentName>
                        {student.first_name} {student.last_name}
                      </StudentName>
                      <StudentEmail>{student.email}</StudentEmail>
                    </StudentDetails>
                  </StudentInfo>
                  <Statistics>
                    <StatItem>
                      <StatLabel>Total Items</StatLabel>
                      <StatValue>{stats.totalItems || 0}</StatValue>
                    </StatItem>
                    <StatItem>
                      <StatLabel>Average Score</StatLabel>
                      <StatValue className="average">
                        {stats.averageScore !== undefined && stats.averageScore !== null 
                          ? `${stats.averageScore.toFixed(1)}%` 
                          : stats.totalItems > 0 
                            ? '0.0%' 
                            : 'N/A'}
                      </StatValue>
                    </StatItem>
                    <ExpandButton>
                      {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                      {isExpanded ? 'Hide' : 'Show'} Details
                    </ExpandButton>
                  </Statistics>
                </StudentHeader>

                {isExpanded && (
                  <GradesContainer>
                    {statusFilter === 'pending' && (() => {
                      const hasPending = student.assignments?.some(a => !a.submitted_at && a.status !== 'graded') ||
                                          student.quizzes?.some(q => !q.submitted_at);
                      if (!hasPending && selectedItems.size === 0) return null;
                      
                      return (
                        <BulkActionsBar>
                          {selectedItems.size > 0 ? (
                            <>
                              <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                                {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
                              </span>
                              <BulkActionButton onClick={() => setBulkGradeModal(true)}>
                                <FiEdit3 size={14} />
                                Bulk Grade ({selectedItems.size})
                              </BulkActionButton>
                              <BulkActionButton onClick={clearSelection} className="secondary">
                                Clear Selection
                              </BulkActionButton>
                            </>
                          ) : (
                            <BulkActionButton onClick={selectAllNotStarted}>
                              <FiCheckSquare size={14} />
                              Select All
                            </BulkActionButton>
                          )}
                        </BulkActionsBar>
                      );
                    })()}

                    {/* Assignments Section */}
                    {(() => {
                      const sortedAssignments = [...(student.assignments || [])].sort((a, b) => {
                        const dateA = a.posted_date ? new Date(a.posted_date).getTime() : 0;
                        const dateB = b.posted_date ? new Date(b.posted_date).getTime() : 0;
                        return dateB - dateA;
                      });
                      
                      if (sortedAssignments.length === 0) return null;
                      
                      return (
                        <SectionCard>
                          <SectionHeader>
                            <SectionTitle>
                              <FiFileText size={20} />
                              Assignments
                            </SectionTitle>
                            <SectionCount>{sortedAssignments.length}</SectionCount>
                          </SectionHeader>
                    <GradesList>
                            {sortedAssignments.map((assignment) => {
                        const isNotStarted = !assignment.submitted_at && assignment.status !== 'graded';
                        const itemKey = getItemKey('assignment', student.id, assignment);
                        const isSelected = selectedItems.has(itemKey);
                        
                              // Calculate late status
                              const isLate = assignment.submitted_at && assignment.due_date 
                                ? new Date(assignment.submitted_at) > new Date(assignment.due_date)
                                : false;
                              
                        return (
                          <GradeItem key={assignment.assignment_id || assignment.id}>
                                  {statusFilter === 'pending' && isNotStarted && (
                              <Checkbox onClick={() => toggleItemSelection('assignment', student.id, assignment)}>
                                {isSelected ? <FiCheckSquare size={18} /> : <FiSquare size={18} />}
                              </Checkbox>
                            )}
                                  <GradeInfo>
                                    <GradeTitle>{assignment.title}</GradeTitle>
                                    <GradeMeta data-assignment-meta style={{ alignItems: 'start' }}>
                                      {/* First row: Course and Submitted */}
                                      <span style={{ gridColumn: '1', whiteSpace: 'nowrap' }}>
                                        <MetaLabel>Course</MetaLabel>
                                        <MetaValue>{assignment.course_code || 'N/A'}</MetaValue>
                                      </span>
                                      <span style={{ gridColumn: '2', whiteSpace: 'nowrap' }}>
                                        <MetaLabel>Submitted</MetaLabel>
                                        <MetaValue>{assignment.submitted_at ? formatTimestamp(assignment.submitted_at) : 'Not Available'}</MetaValue>
                                      </span>
                                      {/* Second row: Posted and Due */}
                                      {assignment.posted_date ? (
                                        <span style={{ gridColumn: '1', whiteSpace: 'nowrap' }}>
                                          <MetaLabel>Posted</MetaLabel>
                                          <MetaValue>{formatDate(assignment.posted_date)}</MetaValue>
                                        </span>
                                      ) : (
                                        <span style={{ gridColumn: '1', visibility: 'hidden', whiteSpace: 'nowrap' }}>
                                          <MetaLabel>Posted</MetaLabel>
                                          <MetaValue>Placeholder</MetaValue>
                                        </span>
                                      )}
                                      <span style={{ gridColumn: '2', whiteSpace: 'nowrap' }}>
                                        <MetaLabel>Due</MetaLabel>
                                        <MetaValue>{assignment.due_date ? formatTimestamp(assignment.due_date) : 'Not Specified'}</MetaValue>
                                      </span>
                              </GradeMeta>
                            </GradeInfo>
                            <GradeItemRightSection>
                              <StatusBadgeContainer>
                                {assignment.submitted_at ? (
                                      <StatusBadge className={isLate ? 'late' : 'on-time'}>
                                        {isLate ? 'Late' : 'On Time'}
                                      </StatusBadge>
                                    ) : (
                                      <StatusBadge className="not-submitted">Not Submitted</StatusBadge>
                                    )}
                              </StatusBadgeContainer>
                              <GradeItemActions>
                                    <GradeScore 
                                      className={assignment.status === 'graded' ? 'graded' : assignment.submitted_at ? 'pending' : 'not-started'}
                                      style={(() => {
                                        if (assignment.status === 'graded') {
                                          const score = Number(assignment.grade) || 0;
                                          return { color: score > 0 ? 'var(--success-color)' : 'var(--error-color)' };
                                        } else if (!assignment.submitted_at) {
                                          return { color: 'var(--error-color)' };
                                        }
                                        return { color: 'var(--warning-color)' };
                                      })()}
                                    >
                                      {assignment.status === 'graded' 
                                        ? formatScore(assignment.grade, assignment.max_points)
                                        : assignment.submitted_at
                                        ? 'Pending'
                                        : formatScore(0, assignment.max_points)
                                      }
                                    </GradeScore>
                                    <div style={{ gridColumn: '2', display: 'flex' }}>
                                      {assignment.submitted_at && assignment.id ? (
                                        <ActionButton onClick={() => handleViewResults('assignment', assignment, student)}>
                                          <FiEye size={16} />
                                          View
                                        </ActionButton>
                                      ) : assignment.id ? (
                                        <ActionButton 
                                          style={{ 
                                            visibility: 'hidden', 
                                            pointerEvents: 'none',
                                            width: '60px',
                                            flexShrink: 0
                                          }} 
                                          disabled
                                        >
                                          <FiEye size={16} />
                                          View
                                        </ActionButton>
                                      ) : null}
                                    </div>
                                    <div style={{ gridColumn: '3', display: 'flex' }}>
                                      <ActionButton 
                                        onClick={() => handleOverride('assignment', assignment, student)}
                                        disabled={!assignment.submitted_at}
                                        style={!assignment.submitted_at ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                      >
                                        <FiEdit3 size={16} />
                                        {assignment.status === 'graded' ? 'Override' : 'Grade'}
                                      </ActionButton>
                                    </div>
                            </GradeItemActions>
                            </GradeItemRightSection>
                          </GradeItem>
                        );
                      })}
                          </GradesList>
                        </SectionCard>
                      );
                    })()}

                    {/* Quizzes Section */}
                    {(() => {
                      const sortedQuizzes = [...(student.quizzes || [])].sort((a, b) => {
                        const dateA = a.assigned_at ? new Date(a.assigned_at).getTime() : 0;
                        const dateB = b.assigned_at ? new Date(b.assigned_at).getTime() : 0;
                        return dateB - dateA;
                      });
                      
                      if (sortedQuizzes.length === 0) return null;
                      
                      return (
                        <SectionCard>
                          <SectionHeader>
                            <SectionTitle>
                              <FiBook size={20} />
                              Quizzes
                            </SectionTitle>
                            <SectionCount>{sortedQuizzes.length}</SectionCount>
                          </SectionHeader>
                          <GradesList>
                            {sortedQuizzes.map((quiz) => {
                              const isNotStarted = !quiz.submitted_at && !quiz.started_at;
                        const itemKey = getItemKey('quiz', student.id, quiz);
                        const isSelected = selectedItems.has(itemKey);
                        
                              // Calculate late status
                              const isLate = quiz.submitted_at && quiz.due_date 
                                ? new Date(quiz.submitted_at) > new Date(quiz.due_date)
                                : false;
                              
                        return (
                          <GradeItem key={quiz.assignment_id || quiz.id}>
                                  {statusFilter === 'pending' && isNotStarted && (
                              <Checkbox onClick={() => toggleItemSelection('quiz', student.id, quiz)}>
                                {isSelected ? <FiCheckSquare size={18} /> : <FiSquare size={18} />}
                              </Checkbox>
                            )}
                                  <GradeInfo>
                                    <GradeTitle>{quiz.title}</GradeTitle>
                                    <GradeMeta data-assignment-meta>
                                      {/* First row: Course and Submitted */}
                                      <span style={{ gridColumn: '1', whiteSpace: 'nowrap' }}>
                                        <MetaLabel>Course</MetaLabel>
                                        <MetaValue>{quiz.course_code || 'N/A'}</MetaValue>
                                      </span>
                                      <span style={{ gridColumn: '2', whiteSpace: 'nowrap' }}>
                                        <MetaLabel>Submitted</MetaLabel>
                                        <MetaValue>{quiz.submitted_at ? formatTimestamp(quiz.submitted_at) : 'Not Available'}</MetaValue>
                                      </span>
                                      {/* Second row: Assigned and Due */}
                                      {quiz.assigned_at ? (
                                        <span style={{ gridColumn: '1', whiteSpace: 'nowrap' }}>
                                          <MetaLabel>Assigned</MetaLabel>
                                          <MetaValue>{formatDate(quiz.assigned_at)}</MetaValue>
                                        </span>
                                      ) : (
                                        <span style={{ gridColumn: '1', visibility: 'hidden', whiteSpace: 'nowrap' }}>
                                          <MetaLabel>Assigned</MetaLabel>
                                          <MetaValue>Placeholder</MetaValue>
                                        </span>
                                      )}
                                      <span style={{ gridColumn: '2', whiteSpace: 'nowrap' }}>
                                        <MetaLabel>Due</MetaLabel>
                                        <MetaValue>{quiz.due_date ? formatTimestamp(quiz.due_date) : 'Not Specified'}</MetaValue>
                                      </span>
                              </GradeMeta>
                            </GradeInfo>
                            <GradeItemRightSection>
                              <StatusBadgeContainer>
                                {quiz.submitted_at ? (
                                      <StatusBadge className={isLate ? 'late' : 'on-time'}>
                                        {isLate ? 'Late' : 'On Time'}
                                      </StatusBadge>
                                ) : quiz.started_at ? (
                                      <StatusBadge className="in-progress">In Progress</StatusBadge>
                                ) : (
                                      <StatusBadge className="not-submitted">Not Submitted</StatusBadge>
                                    )}
                              </StatusBadgeContainer>
                              <GradeItemActions>
                                    <GradeScore 
                                      className={quiz.score !== null && quiz.submitted_at ? 'graded' : quiz.started_at ? 'pending' : 'not-started'}
                                      style={(() => {
                                        if (quiz.submitted_at && quiz.score !== null) {
                                          const score = Number(quiz.score) || 0;
                                          return { color: score > 0 ? 'var(--success-color)' : 'var(--error-color)' };
                                        } else if (!quiz.submitted_at && !quiz.started_at) {
                                          return { color: 'var(--error-color)' };
                                        }
                                        return { color: 'var(--info-color)' };
                                      })()}
                                    >
                                      {quiz.submitted_at 
                                        ? formatScore(quiz.score, quiz.max_score)
                                        : quiz.started_at
                                        ? 'Pending'
                                        : formatScore(0, quiz.max_score)
                                      }
                                    </GradeScore>
                                    <div style={{ gridColumn: '2', display: 'flex' }}>
                                      {quiz.submitted_at && quiz.id ? (
                                        <ActionButton onClick={() => handleViewResults('quiz', quiz, student)}>
                                          <FiEye size={16} />
                                          View
                                        </ActionButton>
                                      ) : quiz.id ? (
                                        <ActionButton 
                                          style={{ 
                                            visibility: 'hidden', 
                                            pointerEvents: 'none',
                                            width: '60px',
                                            flexShrink: 0
                                          }} 
                                          disabled
                                        >
                                          <FiEye size={16} />
                                          View
                                        </ActionButton>
                                      ) : null}
                                    </div>
                                    <div style={{ gridColumn: '3', display: 'flex' }}>
                                      <ActionButton 
                                        onClick={() => handleOverride('quiz', quiz, student)}
                                        disabled={!quiz.submitted_at}
                                        style={!quiz.submitted_at ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                      >
                                        <FiEdit3 size={16} />
                                        {quiz.submitted_at && quiz.score !== null && quiz.score !== undefined ? 'Override' : 'Grade'}
                                      </ActionButton>
                                    </div>
                            </GradeItemActions>
                            </GradeItemRightSection>
                          </GradeItem>
                        );
                      })}
                          </GradesList>
                        </SectionCard>
                      );
                    })()}

                    {/* Attendance Section - keeping existing structure */}
                    {student.attendance && student.attendance.length > 0 && (
                      <SectionCard>
                        <SectionHeader>
                          <SectionTitle>
                            <FiCalendar size={20} />
                            Attendance
                          </SectionTitle>
                          <SectionCount>
                            {student.attendance.reduce((sum, group) => sum + (group.summary?.total || 0), 0)}
                          </SectionCount>
                        </SectionHeader>
                        <GradesList>
                          {student.attendance.map((group) => {
                            const totalSessions = group.summary.total;
                            const presentCount = group.summary.present || 0;
                            const absentCount = group.summary.absent || 0;
                            const lateCount = group.summary.late || 0;
                            const excusedCount = group.summary.excused || 0;
                            const attendancePercentage = totalSessions > 0 
                              ? ((presentCount / totalSessions) * 100).toFixed(1) 
                              : '0.0';
                            
                            return (
                              <GradeItem key={`${group.type}-${group.id}`}>
                                <GradeInfo>
                                  <GradeTitle>
                                    {group.type === 'course' ? '📚' : '🎯'} {group.name} ({group.code})
                                  </GradeTitle>
                                  <GradeMeta>
                                    <span style={{ fontWeight: 600, color: 'var(--success-color)' }}>
                                      Present: {presentCount}
                                    </span>
                                    <span style={{ fontWeight: 600, color: 'var(--error-color)' }}>
                                      Absent: {absentCount}
                                    </span>
                                    {lateCount > 0 && (
                                      <span style={{ fontWeight: 600, color: 'var(--warning-color)' }}>
                                        Late: {lateCount}
                                      </span>
                                    )}
                                    {excusedCount > 0 && (
                                      <span style={{ fontWeight: 600, color: 'var(--info-color)' }}>
                                        Excused: {excusedCount}
                                      </span>
                                    )}
                                    <span style={{ color: 'var(--text-secondary)' }}>
                                      Total Sessions: {totalSessions}
                                    </span>
                                  </GradeMeta>
                                </GradeInfo>
                                <GradeItemRightSection>
                                  <GradeItemActions>
                                    <GradeScore style={{ 
                                      color: parseFloat(attendancePercentage) > 0 ? 'var(--success-color)' : 'var(--error-color)',
                                      fontWeight: 700,
                                      fontSize: '1.125rem'
                          }}>
                                      {attendancePercentage}%
                                    </GradeScore>
                                    <ActionButton onClick={() => {
                                      setSelectedStudentAttendance({ student, group });
                                      setAttendanceModalOpen(true);
                                    }}>
                                      <FiInfo size={16} />
                                      View Details
                                    </ActionButton>
                                  </GradeItemActions>
                                </GradeItemRightSection>
                              </GradeItem>
                            );
                          })}
                    </GradesList>
                      </SectionCard>
                    )}

                    {/* Course Final Grades - keeping existing structure */}
                    {student.courseGrades && student.courseGrades.length > 0 && (
                      <CourseGradeSection>
                        <SectionHeader>
                          <SectionTitle>Course Final Grade</SectionTitle>
                        </SectionHeader>
                        {student.courseGrades.map((courseGrade) => {
                          const formatDateRange = () => {
                            if (!courseGrade.start_date && !courseGrade.end_date) return null;
                            const start = courseGrade.start_date ? formatDate(courseGrade.start_date) : 'TBD';
                            const end = courseGrade.end_date ? formatDate(courseGrade.end_date) : 'TBD';
                            return `${start} - ${end}`;
                          };
                          
                          return (
                          <CourseGradeItem key={courseGrade.course_id}>
                              <div style={{ gridColumn: '1' }}>
                                <div style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '0.5rem',
                                  marginBottom: '0.25rem',
                                  flexWrap: 'wrap'
                                }}>
                                  <div style={{ fontWeight: 600 }}>
                                {courseGrade.course_name || 'N/A'}
                              </div>
                                  <div style={{ 
                                    fontSize: '0.875rem', 
                                    color: 'var(--text-secondary)',
                                    fontWeight: 500
                                  }}>
                                    ({courseGrade.course_code || 'N/A'})
                              </div>
                            </div>
                                {formatDateRange() && (
                                  <div style={{ 
                                    fontSize: '0.8125rem', 
                                    color: 'var(--text-secondary)',
                                    marginTop: '0.25rem'
                                  }}>
                                    {formatDateRange()}
                              </div>
                                )}
                              </div>
                              <div style={{ gridColumn: '2' }}>
                                <GradeScore style={{
                                  color: courseGrade.grade 
                                    ? (isNaN(Number(courseGrade.grade)) 
                                        ? 'var(--success-color)' // Letter grade - default to green
                                        : (Number(courseGrade.grade) > 0 ? 'var(--success-color)' : 'var(--error-color)')) // Numeric grade
                                    : 'var(--text-muted)'
                                }}>
                                  {courseGrade.grade 
                                    ? (() => {
                                        const letterGrade = isNaN(Number(courseGrade.grade)) 
                                          ? courseGrade.grade 
                                          : null;
                                        const numericGrade = courseGrade.numeric_grade 
                                          ? `${courseGrade.numeric_grade}%` 
                                          : null;
                                        
                                        if (letterGrade && numericGrade) {
                                          return `${letterGrade} (${numericGrade})`;
                                        } else if (letterGrade) {
                                          return letterGrade;
                                        } else if (numericGrade) {
                                          return numericGrade;
                                        } else {
                                          return `${courseGrade.grade}%`;
                                        }
                                      })()
                                    : 'Pending'}
                                </GradeScore>
                              </div>
                              <div style={{ gridColumn: '3' }}>
                                <ActionButton onClick={() => {
                                handleOverride('course', { 
                                  course_id: courseGrade.course_id,
                                  course_name: courseGrade.course_name,
                                  course_code: courseGrade.course_code
                                }, student);
                              }}>
                                  <FiEdit3 size={16} />
                                {courseGrade.grade ? 'Edit' : 'Assign'}
                                </ActionButton>
                            </div>
                          </CourseGradeItem>
                          );
                        })}
                      </CourseGradeSection>
                    )}

                    {/* Summary Statistics - keeping existing structure */}
                    {student.statistics && (() => {
                      const calculateAttendanceAverages = () => {
                        if (!student.attendance || student.attendance.length === 0) {
                          return { course: null, elective: null };
                        }
                        
                        let coursePresent = 0;
                        let courseTotal = 0;
                        let electivePresent = 0;
                        let electiveTotal = 0;
                        let courseCount = 0;
                        let electiveCount = 0;
                        
                        student.attendance.forEach(group => {
                          if (group.type === 'course') {
                            coursePresent += group.summary.present || 0;
                            courseTotal += group.summary.total || 0;
                            courseCount++;
                          } else if (group.type === 'elective') {
                            electivePresent += group.summary.present || 0;
                            electiveTotal += group.summary.total || 0;
                            electiveCount++;
                          }
                        });
                        
                        return {
                          course: courseTotal > 0 ? (coursePresent / courseTotal) * 100 : null,
                          elective: electiveTotal > 0 ? (electivePresent / electiveTotal) * 100 : null,
                          courseCount,
                          electiveCount,
                          coursePresent,
                          courseTotal,
                          electivePresent,
                          electiveTotal
                        };
                      };
                      
                      const attendanceData = calculateAttendanceAverages();
                      
                      // Calculate average score for assignments (including unsubmitted as 0)
                      const calculateAssignmentAverage = () => {
                        if (!student.assignments || student.assignments.length === 0) {
                          return null;
                        }
                        
                        // Include all assignments, treating unsubmitted/ungraded as 0
                        const allAssignments = student.assignments.filter(a => a.max_points > 0);
                        
                        if (allAssignments.length === 0) {
                          return null;
                        }
                        
                        const totalScore = allAssignments.reduce((sum, a) => {
                          if (a.status === 'graded' && a.grade !== null && a.grade !== undefined) {
                            const score = Number(a.grade) || 0;
                            const maxPoints = Number(a.max_points) || 1;
                            return sum + (score / maxPoints) * 100;
                          } else {
                            // Not submitted or not graded = 0
                            return sum + 0;
                          }
                        }, 0);
                        
                        return totalScore / allAssignments.length;
                      };
                      
                      // Calculate average score for quizzes (including unsubmitted as 0)
                      const calculateQuizAverage = () => {
                        if (!student.quizzes || student.quizzes.length === 0) {
                          return null;
                        }
                        
                        // Include all quizzes, treating unsubmitted as 0
                        const allQuizzes = student.quizzes.filter(q => q.max_score > 0);
                        
                        if (allQuizzes.length === 0) {
                          return null;
                        }
                        
                        const totalScore = allQuizzes.reduce((sum, q) => {
                          if (q.submitted_at && q.score !== null && q.score !== undefined) {
                            const score = Number(q.score) || 0;
                            const maxScore = Number(q.max_score) || 1;
                            return sum + (score / maxScore) * 100;
                          } else {
                            // Not submitted = 0
                            return sum + 0;
                          }
                        }, 0);
                        
                        return totalScore / allQuizzes.length;
                      };
                      
                      const assignmentAverage = calculateAssignmentAverage();
                      const quizAverage = calculateQuizAverage();
                      
                      return (
                        <SummaryGrid>
                          <div style={{ flex: '0 0 auto', minWidth: '200px' }}>
                            <div style={{ 
                              fontSize: '0.7rem', 
                              color: 'var(--text-secondary)',
                              marginBottom: '0.2rem',
                              fontWeight: 500
                            }}>
                              Overall Average Score
                            </div>
                            <div style={{ 
                              fontSize: '1.1rem', 
                              fontWeight: 700, 
                              color: (() => {
                                const score = student.statistics.averageScore !== undefined && student.statistics.averageScore !== null 
                                  ? student.statistics.averageScore 
                                  : student.statistics.totalItems > 0 
                                    ? 0 
                                    : null;
                                if (score === null) return 'var(--text-muted)';
                                return score > 0 ? 'var(--success-color)' : 'var(--error-color)';
                              })(),
                              marginBottom: '0.3rem'
                            }}>
                              {student.statistics.averageScore !== undefined && student.statistics.averageScore !== null 
                                ? `${student.statistics.averageScore.toFixed(1)}%` 
                                : student.statistics.totalItems > 0 
                                  ? '0.0%' 
                                  : 'N/A'}
                            </div>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '0.75rem 1.5rem',
                              fontSize: '0.7rem',
                              color: 'var(--text-secondary)'
                            }}>
                              <div>
                                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                  {student.statistics.totalItems || 0}
                                </div>
                                <div>Total Items</div>
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                  {student.statistics.gradedAssignments || 0}
                                </div>
                                <div>Graded</div>
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                  {student.statistics.completedQuizzes || 0}
                                </div>
                                <div>Completed</div>
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                                  {(() => {
                                    const unsubmittedAssignments = (student.assignments || []).filter(a => 
                                      a.max_points > 0 && (!a.submitted_at || a.status !== 'graded')
                                    ).length;
                                    const unsubmittedQuizzes = (student.quizzes || []).filter(q => 
                                      q.max_score > 0 && !q.submitted_at
                                    ).length;
                                    return unsubmittedAssignments + unsubmittedQuizzes;
                                  })()}
                                </div>
                                <div>Unsubmitted</div>
                              </div>
                            </div>
                          </div>
                          
                          {assignmentAverage !== null && (
                            <div style={{ flex: '0 0 auto', minWidth: '180px' }}>
                              <div style={{ 
                                fontSize: '0.7rem', 
                                color: 'var(--text-secondary)',
                                marginBottom: '0.2rem',
                                fontWeight: 500
                              }}>
                                Avg Score (Assignments)
                              </div>
                              <div style={{ 
                                fontSize: '1.1rem', 
                                fontWeight: 700, 
                                color: assignmentAverage > 0 ? 'var(--success-color)' : 'var(--error-color)',
                                marginBottom: '0.3rem'
                              }}>
                                {assignmentAverage.toFixed(1)}%
                              </div>
                              <div style={{
                                fontSize: '0.7rem',
                                color: 'var(--text-secondary)'
                              }}>
                                {student.assignments.filter(a => a.max_points > 0).length} assignment(s)
                              </div>
                            </div>
                          )}
                          
                          {quizAverage !== null && (
                            <div style={{ flex: '0 0 auto', minWidth: '180px' }}>
                              <div style={{ 
                                fontSize: '0.7rem', 
                                color: 'var(--text-secondary)',
                                marginBottom: '0.2rem',
                                fontWeight: 500
                              }}>
                                Avg Score (Quizzes)
                              </div>
                              <div style={{ 
                                fontSize: '1.1rem', 
                                fontWeight: 700, 
                                color: quizAverage > 0 ? 'var(--success-color)' : 'var(--error-color)',
                                marginBottom: '0.3rem'
                              }}>
                                {quizAverage.toFixed(1)}%
                              </div>
                              <div style={{
                                fontSize: '0.7rem',
                                color: 'var(--text-secondary)'
                              }}>
                                {student.quizzes.filter(q => q.max_score > 0).length} quiz(zes)
                              </div>
                            </div>
                          )}
                          
                          {attendanceData.course !== null && (
                            <div style={{ flex: '0 0 auto', minWidth: '180px' }}>
                              <div style={{ 
                                fontSize: '0.7rem', 
                                color: 'var(--text-secondary)',
                                marginBottom: '0.2rem',
                                fontWeight: 500
                              }}>
                                Attendance Avg (Courses)
                              </div>
                              <div style={{ 
                                fontSize: '1.1rem', 
                                fontWeight: 700, 
                                color: attendanceData.course > 0 ? 'var(--success-color)' : 'var(--error-color)',
                                marginBottom: '0.3rem'
                              }}>
                                {attendanceData.course.toFixed(1)}%
                              </div>
                              <div style={{
                                fontSize: '0.7rem',
                                color: 'var(--text-secondary)'
                              }}>
                                {attendanceData.coursePresent} / {attendanceData.courseTotal} sessions
                              </div>
                            </div>
                          )}
                          
                          {attendanceData.elective !== null && (
                            <div style={{ flex: '0 0 auto', minWidth: '180px' }}>
                              <div style={{ 
                                fontSize: '0.7rem', 
                                color: 'var(--text-secondary)',
                                marginBottom: '0.2rem',
                                fontWeight: 500
                              }}>
                                Attendance Avg (Electives)
                              </div>
                              <div style={{ 
                                fontSize: '1.1rem', 
                                fontWeight: 700, 
                                color: attendanceData.elective > 0 ? 'var(--success-color)' : 'var(--error-color)',
                                marginBottom: '0.3rem'
                              }}>
                                {attendanceData.elective.toFixed(1)}%
                              </div>
                              <div style={{
                                fontSize: '0.7rem',
                                color: 'var(--text-secondary)'
                              }}>
                                {attendanceData.electivePresent} / {attendanceData.electiveTotal} sessions
                              </div>
                            </div>
                          )}
                        </SummaryGrid>
                      );
                    })()}
                  </GradesContainer>
                )}
              </StudentCard>
            );
          })}
        </StudentsList>
      )}
        </TabContent>

        {/* Add External Item Tab */}
        <TabContent $active={activeTab === 'add-external-item'}>
          {!courseId ? (
            <EmptyState>
              <FiPlus size={32} />
              <h3>Select a Course</h3>
              <p>Please choose a specific course from the toolbar above before creating an external item.</p>
            </EmptyState>
          ) : showExternalItemModal ? null : (
            <EmptyState>
              <FiPlus size={32} />
              <h3>Add External Item</h3>
              <p>Click the button above or use the tab to create a new external item for this course.</p>
            </EmptyState>
          )}
        </TabContent>

        {/* Bulk Grade Tab */}
        <TabContent $active={isBulkGradeActive}>
          {!courseId ? (
            <EmptyState>
              <FiUsers size={32} />
              <h3>Select a Course</h3>
              <p>Please choose a specific course from the toolbar above before opening bulk grading.</p>
            </EmptyState>
          ) : (
            <EmptyState>
              <FiUsers size={32} />
              <h3>Bulk Grade</h3>
              <p>The bulk grading workspace will open automatically.</p>
            </EmptyState>
          )}
        </TabContent>

        {/* Create Rubric Tab */}
        <TabContent $active={activeTab === 'create-rubric'}>
          {rubricBuilderOpen && !editingRubricData ? (
            <RubricBuilder
              courseId={courseId}
              editingRubric={null}
              onCancel={() => {
                setRubricBuilderOpen(false);
                setEditingRubricData(null);
              }}
              onSave={() => {
                setRubricBuilderOpen(false);
                setEditingRubricData(null);
                setRefreshRubrics(!refreshRubrics);
              }}
            />
          ) : !rubricBuilderOpen ? (
            <EmptyState>
              <FiEdit3 size={32} />
              <h3>Create New Rubric</h3>
              <p>Create a new grading rubric to define how student grades are calculated</p>
              <button
                onClick={() => {
                  setRubricBuilderOpen(true);
                  setEditingRubricData(null);
                }}
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--primary-dark)';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'var(--primary-color)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                Create New Rubric
              </button>
            </EmptyState>
          ) : null}
        </TabContent>

        {/* Edit Rubric Tab */}
        <TabContent $active={activeTab === 'edit-rubric'}>
          {rubricBuilderOpen && editingRubricData ? (
            <RubricBuilder
              courseId={courseId}
              editingRubric={editingRubricData}
              onCancel={() => {
                setRubricBuilderOpen(false);
                setEditingRubricData(null);
              }}
              onSave={() => {
                setRubricBuilderOpen(false);
                setEditingRubricData(null);
                setRefreshRubrics(!refreshRubrics);
              }}
            />
          ) : (
            <RubricSelector
              courseId={courseId}
              showEditButton={true}
              onRubricSelected={(rubricData) => {
                setEditingRubricData(rubricData);
                setRubricBuilderOpen(true);
              }}
            />
          )}
        </TabContent>

        {/* External Items Tab */}
        <TabContent $active={activeTab === 'external-items'}>
          {courseId && courseId !== 'all' ? (
            <ExternalItemsManager courseId={courseId} />
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              <p>Please select a specific course to view external items.</p>
            </div>
          )}
        </TabContent>

          {/* Bulk Grading Overlay */}
      {isBulkGradeActive && courseId && (
        <BulkGrading
          courseId={courseId}
          onCancel={closeBulkGrade}
        />
      )}

      {/* External Items Modal */}
      {showExternalItemModal && courseId && (
        <ExternalItemModal
          courseId={courseId}
          onClose={() => {
            setShowExternalItemModal(false);
            // Return to the first tab if we were on add-external-item
            if (activeTab === 'add-external-item') {
              setActiveTab('pending-grading');
            }
          }}
          onSuccess={() => {
            setShowExternalItemModal(false);
            setActiveTab('external-items');
          }}
        />
      )}

      {/* Override Modal */}
      {overrideModal && (
        <ModalOverlay onClick={() => {
          if (!saving) {
            setOverrideModal(null);
            setOverrideScore('');
            setOverrideNumericGrade('');
            setOverrideNotes('');
          }
        }}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {overrideModal.type === 'assignment' ? 'Override Assignment Grade' :
                 overrideModal.type === 'quiz' ? 'Override Quiz Grade' :
                 'Assign Course Grade'}
              </ModalTitle>
              <CloseButton onClick={() => {
                if (!saving) {
                  setOverrideModal(null);
                  setOverrideScore('');
                  setOverrideNumericGrade('');
                  setOverrideNotes('');
                }
              }}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            {/* Rubric Calculation Section for Course Grades */}
            {overrideModal.type === 'course' && (
              <FormGroup>
                <Label>Calculate Grade Using Rubric (Optional)</Label>
                {availableRubrics.length > 0 ? (
                  availableRubrics.length === 1 ? (
                    // Single rubric: Show auto-calculated result
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ 
                        fontSize: '0.875rem', 
                        color: 'var(--text-secondary)', 
                        marginBottom: '0.5rem',
                        padding: '0.75rem',
                        backgroundColor: 'var(--background-secondary)',
                        borderRadius: '12px',
                        border: '1px solid var(--border-color)'
                      }}>
                        <strong>Using rubric:</strong> {availableRubrics[0].name}
                        {isCalculatingGrade && (
                          <span style={{ marginLeft: '0.5rem', color: 'var(--primary-color)' }}>
                            Calculating...
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    // Multiple rubrics: Show dropdown
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                      <select
                        value={selectedRubricForCalculation || ''}
                        onChange={(e) => setSelectedRubricForCalculation(e.target.value || null)}
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          border: '1px solid var(--border-color)',
                          borderRadius: '12px',
                          fontSize: '0.875rem',
                          fontFamily: 'inherit',
                          backgroundColor: 'var(--background-primary)',
                          color: 'var(--text-primary)'
                        }}
                      >
                        <option value="">Select a rubric...</option>
                        {availableRubrics.map((rubric) => (
                          <option key={rubric.id} value={rubric.id}>
                            {rubric.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={handleCalculateGrade}
                        disabled={!selectedRubricForCalculation || isCalculatingGrade}
                        style={{
                          padding: '0.75rem 1.5rem',
                          backgroundColor: selectedRubricForCalculation && !isCalculatingGrade ? 'var(--primary-color)' : 'var(--background-secondary)',
                          color: selectedRubricForCalculation && !isCalculatingGrade ? 'white' : 'var(--text-secondary)',
                          border: 'none',
                          borderRadius: '12px',
                          cursor: selectedRubricForCalculation && !isCalculatingGrade ? 'pointer' : 'not-allowed',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          transition: 'all 0.2s',
                          boxShadow: selectedRubricForCalculation && !isCalculatingGrade ? 'var(--shadow-sm)' : 'none'
                        }}
                      >
                        {isCalculatingGrade ? 'Calculating...' : 'Calculate'}
                      </button>
                    </div>
                  )
                ) : (
                  <div style={{ 
                    fontSize: '0.875rem', 
                    color: 'var(--text-secondary)', 
                    marginBottom: '1rem',
                    padding: '0.75rem',
                    backgroundColor: 'var(--background-secondary)',
                    borderRadius: '12px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ marginBottom: '0.5rem' }}>
                      No rubrics available for this course
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        // Close the current modal
                        setOverrideModal(null);
                        setOverrideScore('');
                        setOverrideNumericGrade('');
                        setOverrideNotes('');
                        setCalculatedGradeData(null);
                        setSelectedRubricForCalculation(null);
                        // Switch to Create Rubric tab and open builder
                        setActiveTab('create-rubric');
                        setRubricBuilderOpen(true);
                        setEditingRubricData(null);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.5rem 1rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: 'var(--primary-color)',
                        backgroundColor: 'transparent',
                        border: '1px solid var(--primary-color)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        textDecoration: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'var(--primary-color)';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = 'var(--primary-color)';
                      }}
                    >
                      <FiEdit3 size={16} />
                      Create Rubric
                    </button>
                  </div>
                )}

                {/* Display Calculated Grade Breakdown */}
                {calculatedGradeData && (
                  <div style={{
                    backgroundColor: 'rgba(46, 213, 115, 0.08)',
                    border: '1px solid rgba(46, 213, 115, 0.3)',
                    borderRadius: '12px',
                    padding: '1rem',
                    marginBottom: '1rem',
                    boxShadow: 'var(--shadow-sm)'
                  }}>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong>Calculated Result:</strong>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Final Percentage</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--primary-color)' }}>
                          {calculatedGradeData.finalPercentage.toFixed(2)}%
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Letter Grade</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--primary-color)' }}>
                          {calculatedGradeData.letterGrade}
                        </div>
                      </div>
                    </div>
                    {calculatedGradeData.categoryBreakdown && calculatedGradeData.categoryBreakdown.length > 0 && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', maxHeight: '150px', overflowY: 'auto' }}>
                        <strong>Category Breakdown:</strong>
                        {calculatedGradeData.categoryBreakdown.map((cat, idx) => (
                          <div key={idx} style={{ marginTop: '0.25rem', paddingLeft: '0.5rem', borderLeft: '2px solid var(--primary-color)' }}>
                            {cat.categoryName}: {cat.categoryPercentage.toFixed(1)}% ({cat.weight}% weight)
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </FormGroup>
            )}

            <FormGroup>
              <Label>
                {overrideModal.type === 'course' ? 'GRADE (LETTER)' : 'Score'}
              </Label>
              <Input
                type={overrideModal.type === 'course' ? 'text' : 'number'}
                step={overrideModal.type === 'course' ? undefined : '0.01'}
                min={overrideModal.type === 'course' ? undefined : '0'}
                value={overrideScore}
                onChange={(e) => setOverrideScore(e.target.value)}
                placeholder={overrideModal.type === 'course' ? '' : 'Enter score'}
              />
              {overrideModal.type === 'course' && (
                <div style={{ 
                  display: 'flex', 
                  gap: '0.5rem', 
                  marginTop: '0.75rem',
                  flexWrap: 'wrap'
                }}>
                  {['A', 'B', 'C', 'F'].map((grade) => (
                    <button
                      key={grade}
                      type="button"
                      onClick={() => setOverrideScore(grade)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        border: '1px solid var(--border-color)',
                        borderRadius: '12px',
                        backgroundColor: overrideScore === grade ? 'var(--primary-color)' : 'white',
                        color: overrideScore === grade ? 'white' : 'var(--text-primary)',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        minWidth: '45px'
                      }}
                      onMouseEnter={(e) => {
                        if (overrideScore !== grade) {
                          e.target.style.backgroundColor = 'var(--bg-secondary)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (overrideScore !== grade) {
                          e.target.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      {grade}
                    </button>
                  ))}
                </div>
              )}
              {overrideModal.type === 'course' && (
                <FormGroup style={{ marginTop: '1rem' }}>
                  <Label>Numeric Grade (Optional)</Label>
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: 'var(--text-secondary)', 
                    marginBottom: '0.5rem'
                  }}>
                    Note: If numeric grade is provided, it will be displayed as A (95%) or A if numeric grade is not provided.
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={overrideNumericGrade}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                        setOverrideNumericGrade(value);
                      }
                    }}
                    placeholder="Enter numeric grade (0-100)"
                  />
                  <div style={{ 
                    display: 'flex', 
                    gap: '0.5rem', 
                    marginTop: '0.75rem',
                    flexWrap: 'wrap'
                  }}>
                    {[70, 75, 80, 85, 95, 100].map((numGrade) => (
                      <button
                        key={numGrade}
                        type="button"
                        onClick={() => setOverrideNumericGrade(numGrade.toString())}
                        style={{
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          border: '1px solid var(--border-color)',
                          borderRadius: '12px',
                          backgroundColor: overrideNumericGrade === numGrade.toString() ? 'var(--primary-color)' : 'white',
                          color: overrideNumericGrade === numGrade.toString() ? 'white' : 'var(--text-primary)',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          minWidth: '45px'
                        }}
                        onMouseEnter={(e) => {
                          if (overrideNumericGrade !== numGrade.toString()) {
                            e.target.style.backgroundColor = 'var(--bg-secondary)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (overrideNumericGrade !== numGrade.toString()) {
                            e.target.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        {numGrade}
                      </button>
                    ))}
                  </div>
                </FormGroup>
              )}
            </FormGroup>
            <FormGroup>
              <Label>{overrideModal.type === 'course' ? 'Feedback (Optional)' : 'Notes (optional)'}</Label>
              <Textarea
                value={overrideNotes}
                onChange={(e) => setOverrideNotes(e.target.value)}
                placeholder={overrideModal.type === 'course' ? '' : 'Add notes about this grade...'}
              />
            </FormGroup>
            <ButtonGroup>
              <Button className="secondary" onClick={() => {
                setOverrideModal(null);
                setOverrideScore('');
                setOverrideNumericGrade('');
                setOverrideNotes('');
                setCalculatedGradeData(null);
                setSelectedRubricForCalculation(null);
              }} disabled={saving}>
                Cancel
              </Button>
              <Button className="primary" onClick={handleSaveOverride} disabled={saving}>
                {saving ? 'Saving...' : (overrideModal.type === 'course' && calculatedGradeData ? 'Assign' : 'Save')}
              </Button>
            </ButtonGroup>
          </Modal>
        </ModalOverlay>
      )}

      {/* Bulk Grade Modal */}
      {bulkGradeModal && (
        <ModalOverlay onClick={() => !bulkGrading && setBulkGradeModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Bulk Grade Not Submitted Items</ModalTitle>
              <CloseButton onClick={() => !bulkGrading && setBulkGradeModal(false)}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            <div style={{ marginBottom: '1rem' }}>
              <strong>Selected Items:</strong> {selectedItems.size}
              <br />
              <small style={{ color: 'var(--text-secondary)' }}>
                This will set the grade for all selected "Not Submitted" assignments and quizzes.
              </small>
            </div>

            <FormGroup>
              <Label>Score</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={bulkGradeScore}
                onChange={(e) => setBulkGradeScore(e.target.value)}
                placeholder="Enter score"
              />
            </FormGroup>
            <FormGroup>
              <Label>Notes (optional)</Label>
              <Textarea
                value={bulkGradeNotes}
                onChange={(e) => setBulkGradeNotes(e.target.value)}
                placeholder="Add notes about this grade..."
              />
            </FormGroup>
            <ButtonGroup>
              <Button className="secondary" onClick={() => setBulkGradeModal(false)} disabled={bulkGrading}>
                Cancel
              </Button>
              <Button className="primary" onClick={handleBulkGrade} disabled={bulkGrading}>
                {bulkGrading ? 'Grading...' : `Grade ${selectedItems.size} Item${selectedItems.size !== 1 ? 's' : ''}`}
              </Button>
            </ButtonGroup>
          </Modal>
        </ModalOverlay>
      )}

      {/* Attendance Details Modal - keeping existing structure */}
      {attendanceModalOpen && selectedStudentAttendance && (
        <ModalOverlay onClick={() => setAttendanceModalOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <ModalHeader>
              <ModalTitle>
                Attendance Details - {selectedStudentAttendance.student.first_name} {selectedStudentAttendance.student.last_name}
              </ModalTitle>
              <CloseButton onClick={() => setAttendanceModalOpen(false)}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>
            <div>
              <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>
                {selectedStudentAttendance.group.name} ({selectedStudentAttendance.group.code})
              </h4>
              <div style={{ marginBottom: '1rem', padding: '1rem', background: 'var(--background-secondary)', borderRadius: '12px'}}>
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 600, color: 'var(--success-color)' }}>
                    Present: {selectedStudentAttendance.group.summary.present || 0}
                  </span>
                  <span style={{ fontWeight: 600, color: 'var(--error-color)' }}>
                    Absent: {selectedStudentAttendance.group.summary.absent || 0}
                  </span>
                  {(selectedStudentAttendance.group.summary.late || 0) > 0 && (
                    <span style={{ fontWeight: 600, color: 'var(--warning-color)' }}>
                      Late: {selectedStudentAttendance.group.summary.late || 0}
                    </span>
                  )}
                  {(selectedStudentAttendance.group.summary.excused || 0) > 0 && (
                    <span style={{ fontWeight: 600, color: 'var(--info-color)' }}>
                      Excused: {selectedStudentAttendance.group.summary.excused || 0}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Total Sessions: {selectedStudentAttendance.group.summary.total || 0}
                </div>
              </div>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>Session</th>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>Date</th>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>Time</th>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>Check-in</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedStudentAttendance.group.records?.map((record, idx) => {
                      const statusColors = {
                        'present': { bg: 'rgba(104, 211, 145, 0.1)', color: 'var(--success-dark)', icon: <FiCheckCircle /> },
                        'absent': { bg: 'rgba(252, 129, 129, 0.1)', color: 'var(--error-color)', icon: <FiXCircle /> },
                        'late': { bg: 'rgba(246, 224, 94, 0.1)', color: 'var(--warning-dark)', icon: <FiClock /> },
                        'excused': { bg: 'rgba(96, 165, 250, 0.1)', color: 'var(--info-color)', icon: <FiInfo /> }
                      };
                      const statusStyle = statusColors[record.status] || statusColors.absent;
                      
                      return (
                        <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{record.session_title || 'N/A'}</td>
                          <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{formatDate(record.date)}</td>
                          <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                            {record.start_time && record.end_time 
                              ? `${record.start_time} - ${record.end_time}`
                              : 'N/A'}
                          </td>
                          <td style={{ padding: '0.75rem' }}>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '999px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              backgroundColor: statusStyle.bg,
                              color: statusStyle.color
                            }}>
                              {statusStyle.icon}
                              {record.status?.charAt(0).toUpperCase() + record.status?.slice(1) || 'Unknown'}
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                            {record.checked_in_at ? formatTimestamp(record.checked_in_at) : 'N/A'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </Modal>
        </ModalOverlay>
      )}

      <SuccessModal
        isOpen={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        title="Success"
        message={successMessage}
      />

      <SubmissionViewModal
        isOpen={submissionModalOpen}
        onClose={() => {
          setSubmissionModalOpen(false);
          setSubmissionModalData(null);
        }}
        type={submissionModalData?.type}
        item={submissionModalData?.item}
        student={submissionModalData?.student}
      />
          </Container>
        </MainContent>
      </DashboardContainer>
    </PageWrapper>
  );
}

export default Gradebook;

